'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, MapPin, Search, X } from 'lucide-react';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';

interface LocationPickerMapProps {
  onLocationSelect: (location: {
    lat: number;
    lng: number;
    country?: string;
    city?: string;
    street?: string;
    formattedAddress?: string;
  }) => void;
  onClose: () => void;
  initialLocation?: { lat: number; lng: number };
  showControls?: boolean;
}

export default function LocationPickerMap({
  onLocationSelect,
  onClose,
  initialLocation,
  showControls = true,
}: LocationPickerMapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const isScriptLoaded = useGoogleMaps();
  const [isMapReady, setIsMapReady] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  // What the pin currently points at, shown under the map so the user sees
  // their choice register without a confirm step.
  const [address, setAddress] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  onLocationSelectRef.current = onLocationSelect;
  // Drops the result of a geocode that a newer pin move has overtaken.
  const geocodeSeq = useRef(0);

  // The picker opens below the form fields, often off-screen; bring it up
  // so it's obvious something appeared.
  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Initialize the map, marker and Autocomplete exactly once.
  // (Having initialLocation in the deps used to recreate everything on every
  // parent re-render, which piled up stale click listeners on the map div and
  // made dropdown clicks look like map clicks.)
  useEffect(() => {
    if (!isScriptLoaded || !mapRef.current || !window.google?.maps) return;
    if (mapInstanceRef.current) return;

    const defaultCenter = initialLocation || { lat: 29.7604, lng: -95.3698 }; // Downtown Houston, TX

    const map = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstanceRef.current = map;

    const marker = new window.google.maps.Marker({
      position: defaultCenter,
      map,
      title: 'Selected Location',
      draggable: true,
    });

    markerRef.current = marker;
    setIsMapReady(true);

    map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        marker.setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        void commitPosition(e.latLng.lat(), e.latLng.lng());
      }
    });
    marker.addListener('dragend', () => {
      const pos = marker.getPosition();
      if (pos) void commitPosition(pos.lat(), pos.lng());
    });

    if (searchInputRef.current && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { fields: ['geometry', 'formatted_address', 'address_components'] }
      );
      autocomplete.bindTo('bounds', map);
      autocompleteRef.current = autocomplete;

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry?.location) return;
        const location = place.geometry.location;
        map.setCenter(location);
        map.setZoom(16);
        marker.setPosition(location);
        void commitPosition(location.lat(), location.lng(), place.formatted_address);
      });
    }
    // Runs once: the listeners above reach the latest form callback through
    // onLocationSelectRef, so nothing here needs to re-bind.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded]);

  // Keep the marker/map in sync when the caller changes initialLocation —
  // but not when the change is our own pin move echoed back by the form,
  // or every click would also recentre the map under the cursor.
  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current || !initialLocation) return;
    const current = markerRef.current.getPosition();
    if (
      current &&
      Math.abs(current.lat() - initialLocation.lat) < 1e-7 &&
      Math.abs(current.lng() - initialLocation.lng) < 1e-7
    ) {
      return;
    }
    mapInstanceRef.current.setCenter(initialLocation);
    markerRef.current.setPosition(initialLocation);
  }, [initialLocation?.lat, initialLocation?.lng]);

  // Editing something that already has a pin: show its address straight
  // away, without re-reporting the unchanged location to the form.
  useEffect(() => {
    if (!isMapReady || !initialLocation || address) return;
    let cancelled = false;
    reverseGeocode(initialLocation.lat, initialLocation.lng).then((r) => {
      if (!cancelled && r.formattedAddress) setAddress(r.formattedAddress);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady]);

  // Google's .pac-container is appended to document.body, so Radix Dialog sees
  // clicks on a suggestion as "outside" and closes the parent form. Catching
  // these events in the capture phase and stopping them keeps the dialog open.
  useEffect(() => {
    const stopIfInsidePac = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest && target.closest('.pac-container')) {
        e.stopPropagation();
      }
    };
    document.addEventListener('mousedown', stopIfInsidePac, true);
    document.addEventListener('pointerdown', stopIfInsidePac, true);
    document.addEventListener('touchstart', stopIfInsidePac, true);
    return () => {
      document.removeEventListener('mousedown', stopIfInsidePac, true);
      document.removeEventListener('pointerdown', stopIfInsidePac, true);
      document.removeEventListener('touchstart', stopIfInsidePac, true);
    };
  }, []);

  const reverseGeocode = async (
    lat: number,
    lng: number
  ): Promise<{
    formattedAddress?: string;
    country?: string;
    city?: string;
    street?: string;
  }> => {
    if (!window.google?.maps?.Geocoder) {
      return {};
    }
    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat, lng };
    return new Promise((resolve) => {
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const result = results[0];
          const addressComponents = result.address_components || [];
          let country = '';
          let city = '';
          let street = '';
          addressComponents.forEach((component) => {
            const types = component.types;
            if (types.includes('country')) {
              country = component.long_name;
            }
            if (types.includes('locality') || types.includes('administrative_area_level_1')) {
              if (!city) {
                city = component.long_name;
              }
            }
            if (types.includes('street_number') || types.includes('route')) {
              street = component.long_name;
            }
          });
          resolve({
            formattedAddress: result.formatted_address,
            country,
            city,
            street,
          });
        } else {
          resolve({});
        }
      });
    });
  };

  // Reverse-geocodes the pin and hands the location to the form. Called on
  // every click, drag and address pick, so the form always holds the latest
  // pin and "Done" only closes the map.
  const commitPosition = async (lat: number, lng: number, known?: string) => {
    const seq = ++geocodeSeq.current;
    setIsGeocoding(true);
    try {
      const geocoded = known ? { formattedAddress: known } : await reverseGeocode(lat, lng);
      if (seq !== geocodeSeq.current) return;
      setAddress(geocoded.formattedAddress ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      onLocationSelectRef.current({ lat, lng, ...geocoded });
    } catch (error) {
      console.error('Error during geocoding:', error);
      if (seq !== geocodeSeq.current) return;
      setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      onLocationSelectRef.current({ lat, lng });
    } finally {
      if (seq === geocodeSeq.current) setIsGeocoding(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col w-full rounded-2xl border border-[#cdc4c1] bg-white/40 p-4 sm:p-5 scroll-mt-4"
    >
      {showControls && (
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-semibold">
              <MapPin className="h-4 w-4 text-[#948780]" aria-hidden="true" />
              Pin the location
            </h3>
            <p className="text-sm text-[#554E49] mt-0.5">
              Search for an address, or tap the map and drag the pin. The
              location is saved as you go.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close map"
            className="h-8 w-8 p-0 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for an address..."
          aria-label="Search for an address"
          className="w-full rounded-full border border-[#cdc4c1] bg-background px-9 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>
      <div className="relative w-full h-[320px] sm:h-[400px]">
        <div
          ref={mapRef}
          style={{ width: '100%', height: '100%' }}
          className="rounded-xl border border-[#cdc4c1] overflow-hidden"
        />
        {!isMapReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
            <p className="text-gray-500">Loading map...</p>
          </div>
        )}
      </div>
      {showControls && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-3">
          <p
            className="flex items-center gap-2 text-sm text-[#554E49] min-w-0"
            role="status"
            aria-live="polite"
          >
            {isGeocoding ? (
              'Finding address…'
            ) : address ? (
              <>
                <Check className="h-4 w-4 text-green-700 shrink-0" aria-hidden="true" />
                <span className="truncate">{address}</span>
              </>
            ) : (
              'No pin placed yet'
            )}
          </p>
          <Button
            onClick={onClose}
            className="rounded-full bg-[#cdc4c1] text-black hover:opacity-80 shrink-0"
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
